import styled from '@emotion/styled';
import { theme } from '../../styles/theme';

/** Section shell — uses LoginPage.css tokens (--login-form-*) */
export const WebsiteSection = styled.section`
  padding: ${theme.spacing['3xl']} 0;
  scroll-margin-top: 100px;
  position: relative;
  background: ${(props) => (props.$alt ? 'var(--login-form-input-bg)' : 'var(--login-form-bg)')};
`;

export const WebsiteSectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
  position: relative;
`;

export const WebsiteSectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['2xl']};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -${theme.spacing.lg};
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(
      to right,
      transparent,
      var(--login-primary),
      transparent
    );
  }
`;

export const WebsiteSectionTitle = styled.h2`
  margin: 0 0 ${theme.spacing.md};
  font-family: ${theme.typography.fontFamily.sans};
  font-size: clamp(1.5rem, 2.5vw, 1.875rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--login-form-text);
`;

export const WebsiteSectionSubtitle = styled.p`
  margin: 0 auto;
  max-width: 36ch;
  font-size: ${theme.typography.fontSize.lg};
  line-height: 1.55;
  color: var(--login-form-muted);
`;

/** Same surface as .loginPage__card */
export const WebsiteCard = styled.div`
  background: var(--login-form-bg);
  border: 1px solid var(--login-form-border);
  border-radius: 20px;
  box-shadow: var(--login-form-shadow);
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: rgba(72, 138, 236, 0.25);
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1);
    transform: translateY(-2px);
  }
`;

/** Mirrors .loginPage__feature — horizontal icon + copy, light theme for marketing pages */
export const WebsiteLoginFeatureCard = styled.article`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: var(--login-form-bg);
  border: 1px solid var(--login-form-border);
  box-shadow: var(--login-form-shadow);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: rgba(72, 138, 236, 0.25);
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1);
  }
`;

/** Header / nav primary action — pill shape with website blue theme */
export const WebsiteStorefrontPrimaryButton = styled.button`
  width: auto;
  box-sizing: border-box;
  border: none;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 9px 18px;
  background: var(--login-primary, #488aec);
  color: #ffffff;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.2;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  box-shadow:
    0 10px 24px rgba(72, 138, 236, 0.28),
    0 2px 8px rgba(15, 23, 42, 0.08);
  transition: filter 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    filter: none;
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
    flex-shrink: 0;
  }
`;

/** Inline variant of .loginPage__submit (width not forced to 100%) */
export const WebsitePrimaryButton = styled.button`
  width: auto;
  border: none;
  border-radius: 12px;
  padding: 14px 22px;
  background: linear-gradient(135deg, var(--login-primary) 0%, var(--login-primary-dark) 100%);
  color: #ffffff;
  font-family: inherit;
  font-size: ${theme.typography.fontSize.base};
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(72, 138, 236, 0.28);
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 28px rgba(72, 138, 236, 0.34);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

/** Get Started / trial CTAs on marketing pages — centered on mobile (not used in hero) */
export const WebsiteMarketingCtaButton = styled(WebsitePrimaryButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  @media (max-width: 900px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

export const WebsiteMarketingCtaButtonFullWidth = styled(WebsiteMarketingCtaButton)`
  width: 100%;

  @media (max-width: 900px) {
    margin-left: 0;
    margin-right: 0;
  }
`;

export const WebsitePageHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['2xl']};
`;

export const WebsitePageTitle = styled.h1`
  margin: 0 0 ${theme.spacing.md};
  font-size: clamp(1.75rem, 3.5vw, 2.5rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--login-form-text);
`;

export const WebsitePageIntro = styled.p`
  margin: 0 auto;
  max-width: 52ch;
  font-size: ${theme.typography.fontSize.lg};
  line-height: 1.65;
  color: var(--login-form-muted);
`;

export const WebsiteContentBlock = styled.div`
  margin-bottom: ${theme.spacing['2xl']};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const WebsitePageSectionTitle = styled.h2`
  margin: 0 0 ${theme.spacing.lg};
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--login-form-text);
`;

export const WebsiteBodyText = styled.p`
  margin: 0 0 ${theme.spacing.md};
  font-size: ${theme.typography.fontSize.base};
  line-height: 1.65;
  color: var(--login-form-muted);

  &:last-child {
    margin-bottom: 0;
  }
`;

export const WebsiteFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const WebsiteFormLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--login-form-text);
`;

const formFieldStyles = `
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid var(--login-form-border);
  background: var(--login-form-input-bg);
  color: var(--login-form-text);
  font: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--login-primary);
    box-shadow: 0 0 0 3px rgba(72, 138, 236, 0.18);
    background: var(--login-form-bg);
  }
`;

export const WebsiteFormInput = styled.input`
  ${formFieldStyles}
`;

export const WebsiteFormTextarea = styled.textarea`
  ${formFieldStyles}
  min-height: 140px;
  resize: vertical;
`;

export const WebsiteFormAlert = styled.p`
  margin: 0;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.9rem;
  line-height: 1.45;

  ${(props) =>
    props.$variant === 'error'
      ? `
    background: rgba(239, 68, 68, 0.08);
    border: 1px solid rgba(239, 68, 68, 0.18);
    color: #b91c1c;
  `
      : `
    background: rgba(72, 138, 236, 0.1);
    border: 1px solid rgba(72, 138, 236, 0.2);
    color: var(--login-primary-dark);
  `}
`;
